/**
 * 统一的日志管理工具
 * 在生产环境自动禁用日志输出
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = isDevelopment || window.location.search.includes('debug=true');

class Logger {
  constructor() {
    this.isDevelopment = isDevelopment;
    this.isDebugEnabled = isDebugEnabled;
    
    // 在生产环境覆盖console方法
    if (!this.isDevelopment && !this.isDebugEnabled) {
      this.silenceConsole();
    }
  }

  /**
   * 在生产环境禁用console输出
   */
  silenceConsole() {
    const noop = () => {};
    
    // 保存原始方法以备需要
    this.originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
      debug: console.debug,
      trace: console.trace
    };
    
    // 覆盖console方法
    console.log = noop;
    console.debug = noop;
    console.trace = noop;
    
    // 保留error和warn在生产环境，但限制输出
    console.error = (...args) => {
      if (this.shouldLogError(args)) {
        this.originalConsole.error(...args);
      }
    };
    
    console.warn = (...args) => {
      if (this.shouldLogWarning(args)) {
        this.originalConsole.warn(...args);
      }
    };
  }

  /**
   * 判断是否应该输出错误
   */
  shouldLogError(args) {
    // 过滤掉一些不重要的错误
    const message = args[0]?.toString() || '';
    
    // 过滤掉React开发警告
    if (message.includes('React Hook') || message.includes('StrictMode')) {
      return false;
    }
    
    // 过滤掉已知的第三方库警告
    if (message.includes('MUI:') || message.includes('Material-UI:')) {
      return false;
    }
    
    return true;
  }

  /**
   * 判断是否应该输出警告
   */
  shouldLogWarning(args) {
    const message = args[0]?.toString() || '';
    
    // 过滤掉不重要的警告
    if (message.includes('componentWillReceiveProps') || 
        message.includes('componentWillMount')) {
      return false;
    }
    
    return true;
  }

  /**
   * 统一的日志方法
   */
  log(category, message, ...args) {
    if (this.isDebugEnabled) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [${category}]`;
      
      if (this.originalConsole) {
        this.originalConsole.log(prefix, message, ...args);
      } else {
        console.log(prefix, message, ...args);
      }
    }
  }

  error(category, message, ...args) {
    if (this.shouldLogError([message])) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [ERROR] [${category}]`;
      
      if (this.originalConsole) {
        this.originalConsole.error(prefix, message, ...args);
      } else {
        console.error(prefix, message, ...args);
      }
    }
  }

  warn(category, message, ...args) {
    if (this.shouldLogWarning([message])) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [WARN] [${category}]`;
      
      if (this.originalConsole) {
        this.originalConsole.warn(prefix, message, ...args);
      } else {
        console.warn(prefix, message, ...args);
      }
    }
  }

  info(category, message, ...args) {
    if (this.isDebugEnabled) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [INFO] [${category}]`;
      
      if (this.originalConsole) {
        this.originalConsole.info(prefix, message, ...args);
      } else {
        console.info(prefix, message, ...args);
      }
    }
  }

  /**
   * 性能日志
   */
  performance(operation, duration) {
    if (this.isDebugEnabled) {
      this.log('PERFORMANCE', `${operation} took ${duration}ms`);
    }
  }

  /**
   * API调用日志
   */
  api(method, url, data, response) {
    if (this.isDebugEnabled) {
      this.log('API', `${method} ${url}`, { data, response });
    }
  }
}

// 创建单例实例
const logger = new Logger();

// 导出logger实例和便捷方法
export default logger;

// 便捷方法导出
export const log = (category, message, ...args) => logger.log(category, message, ...args);
export const error = (category, message, ...args) => logger.error(category, message, ...args);
export const warn = (category, message, ...args) => logger.warn(category, message, ...args);
export const info = (category, message, ...args) => logger.info(category, message, ...args);
export const performance = (operation, duration) => logger.performance(operation, duration);
export const api = (method, url, data, response) => logger.api(method, url, data, response);